import React, { useState, useEffect, useRef } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { fetchWeatherForDate, geocodeAddress } from "../../_utils/nwsUtils";

export default function NwsWeatherFetcher({ reportDate, onWeatherFetched, disabled }) {
  const [expanded, setExpanded] = useState(false);
  const [locationText, setLocationText] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | locating | fetching | done | error
  const [errorMsg, setErrorMsg] = useState("");

  // Tracks the latest reportDate so an in-flight fetch (e.g. awaiting a slow geolocation
  // prompt) can detect if the user changed the report date before it resolved, instead of
  // silently applying weather for a date that's no longer displayed.
  const reportDateRef = useRef(reportDate);
  useEffect(() => {
    reportDateRef.current = reportDate;
  }, [reportDate]);

  // Guards against applying state after the component has unmounted (e.g. the user
  // navigates away while a fetch or the auto-close timer is still pending).
  const isMountedRef = useRef(true);
  const resetTimeoutRef = useRef(null);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearTimeout(resetTimeoutRef.current);
    };
  }, []);

  const reset = () => {
    setExpanded(false);
    setPhase("idle");
    setErrorMsg("");
  };

  const doFetch = async (lat, lon, requestedDate) => {
    setPhase("fetching");
    try {
      const result = await fetchWeatherForDate(lat, lon, requestedDate);
      if (!isMountedRef.current) return;
      if (reportDateRef.current?.getTime() !== requestedDate?.getTime()) {
        setPhase("error");
        setErrorMsg("Report date changed before weather data was returned. Please try again.");
        return;
      }
      onWeatherFetched(result);
      setPhase("done");
      resetTimeoutRef.current = setTimeout(reset, 1800);
    } catch (e) {
      if (!isMountedRef.current) return;
      setPhase("error");
      setErrorMsg(e.message);
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setPhase("error");
      setErrorMsg("Geolocation is not supported by your browser.");
      return;
    }
    const requestedDate = reportDate;
    setPhase("locating");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (!isMountedRef.current) return;
        doFetch(coords.latitude, coords.longitude, requestedDate);
      },
      (err) => {
        if (!isMountedRef.current) return;
        setPhase("error");
        setErrorMsg("Location access denied. " + err.message);
      }
    );
  };

  const handleManualFetch = async () => {
    if (!locationText.trim()) return;
    const requestedDate = reportDate;
    setPhase("fetching");
    try {
      const { lat, lon } = await geocodeAddress(locationText);
      if (!isMountedRef.current) return;
      await doFetch(lat, lon, requestedDate);
    } catch (e) {
      if (!isMountedRef.current) return;
      setPhase("error");
      setErrorMsg(e.message);
    }
  };

  const isBusy = phase === "locating" || phase === "fetching";

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-outline-info"
        onClick={() => setExpanded(true)}
        disabled={disabled}
        title="Auto-fill weather from National Weather Service"
      >
        Auto-fill from NWS
      </button>

      <Modal isOpen={expanded} toggle={reset} size="sm">
        <ModalHeader toggle={reset}>National Weather Service</ModalHeader>
        <ModalBody>
          <button
            type="button"
            className="btn btn-sm btn-primary btn-block mb-2"
            onClick={handleUseMyLocation}
            disabled={isBusy}
          >
            {phase === "locating" ? (
              <>
                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
                Getting location...
              </>
            ) : (
              "Use My Location"
            )}
          </button>

          <div className="text-center mb-2">
            <small className="text-muted">— or enter location —</small>
          </div>

          <div className="input-group input-group-sm">
            <input
              type="text"
              className="form-control"
              placeholder="Full address, city and state, or ZIP code"
              value={locationText}
              onChange={(e) => {
                setLocationText(e.target.value);
                if (phase === "error") setPhase("idle");
              }}
              onKeyDown={(e) => e.key === "Enter" && handleManualFetch()}
              disabled={isBusy}
            />
            <div className="input-group-append">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleManualFetch}
                disabled={!locationText.trim() || isBusy}
              >
                {phase === "fetching" ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                ) : (
                  "Go"
                )}
              </button>
            </div>
          </div>

          {phase === "error" && (
            <small className="text-danger d-block mt-1">{errorMsg}</small>
          )}
          {phase === "done" && (
            <small className="text-success d-block mt-1">Weather data applied.</small>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
